import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { mirrorArrowsRightToLeft } from "./mirrorArrows";
import en from "./en";

/**
 * Two languages, English and Arabic, and Arabic is written right to left.
 *
 * Two kinds of string live here:
 * - the `translation` namespace — structured keys (`common.cancel`), English in `en.ts`, Arabic in
 *   `ar.ts`;
 * - the `ui` namespace — keyed by the ENGLISH TEXT ITSELF, read through `tx("Save")`. English needs
 *   no bundle at all (a missing key reads back as itself), so every screen written before Arabic
 *   existed is translated by wrapping its text, not by inventing a key per sentence. The Arabic for
 *   it is `ar/ui.json`, generated from the source by `apps/web/scripts/i18n-extract.mjs`.
 *
 * The language is per browser (`bahar.lang`). Switching RELOADS the page: labels built when a module
 * loads (the menu tree, column lists, option lists) would otherwise keep the old language, and the
 * page direction and the style cache are chosen once, at start.
 */

export type Lang = "en" | "ar";
export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

const STORAGE_KEY = "bahar.lang";

export function storedLanguage(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "ar" || v === "en") return v;
  } catch {
    /* storage may be unavailable */
  }
  return "en";
}

export const currentLang: Lang = storedLanguage();
export const isRtl = currentLang === "ar";

export function setLanguage(lang: Lang) {
  if (lang === currentLang) return;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* storage may be unavailable — the switch then lasts until the reload, i.e. not at all */
  }
  window.location.reload();
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: "en",
  fallbackLng: "en",
  ns: ["translation", "ui"],
  defaultNS: "translation",
  interpolation: { escapeValue: false },
  returnNull: false,
  initAsync: false,
});

/**
 * Loads the Arabic bundles (a chunk of their own, so English readers never download them) and
 * switches to Arabic. Awaited by `main.tsx` BEFORE the app's modules are imported, so a label
 * computed when a module loads is already in the reader's language.
 */
export async function initLanguage(): Promise<void> {
  const html = document.documentElement;
  html.lang = currentLang;
  html.dir = isRtl ? "rtl" : "ltr";
  if (currentLang !== "ar") return;
  const [{ default: ar }, { default: ui }] = await Promise.all([import("./ar"), import("./ar/ui.json")]);
  i18n.addResourceBundle("ar", "translation", ar, true, true);
  i18n.addResourceBundle("ar", "ui", ui as Record<string, string>, true, true);
  await i18n.changeLanguage("ar");
  mirrorArrowsRightToLeft();
}

const TX_OPTIONS = { ns: "ui", keySeparator: false, nsSeparator: false } as const;

/**
 * Translates a piece of English UI text. The text IS the key: `tx("Save")`, and with values
 * `tx("{{count}} days", { count })`. English, or text the Arabic bundle has not caught up with,
 * reads back unchanged — so wrapping text can never break an English screen.
 */
export function tx(text: string, values?: Record<string, unknown>): string;
export function tx<T extends string | null | undefined>(text: T, values?: Record<string, unknown>): T;
export function tx(text: string | null | undefined, values?: Record<string, unknown>): string | null | undefined {
  if (!text) return text;
  if (currentLang === "en" && !values) return text;
  return i18n.t(text, { ...TX_OPTIONS, defaultValue: text, ...values }) as string;
}

/** `tx` for a value that may not be text (a label prop can be a node). */
export function txNode<T>(value: T): T {
  return (typeof value === "string" ? tx(value) : value) as T;
}

export default i18n;
